"use client"

import { useEffect, useRef, useState } from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { useId } from "react"
import { ICONE, Icone } from "@/components/design-new/icone"
import { useViaCep } from "@/lib/hooks/use-via-cep"
import type { EnderecoPayload } from "@/lib/services/assinatura-service"
import {
  bandeiraDoCartao,
  errosDoCartao,
  mascaraCodigo,
  mascaraNumeroDoCartao,
  mascaraValidade,
  NOME_DA_BANDEIRA,
  ondeFicaOCodigo,
  tamanhoDoCodigo,
  type CampoDoCartao,
  type CartaoDigitado,
} from "@/lib/utils/cartao"
import { mascaraCep } from "@/lib/utils/cep"
import { faltasDoEndereco, NOME_DO_CAMPO_DE_ENDERECO, type CampoDeEndereco } from "@/lib/utils/endereco"
import { digitosDeTelefoneBR, telefoneBREscrito } from "@/lib/utils/telefone"
import { radiusValue } from "./block-preview"
import type { PageTheme } from "@/lib/services/lead-service"

/**
 * Peças da PÁGINA DO CLIENTE (a de vendas, a pública do personal).
 *
 * Por que não são as peças do painel: a página é do personal, com a cor, a fonte
 * e a forma que ele escolheu. Se ela usasse o [Botao](../design-new/botoes-showcase.tsx)
 * do app, toda página de todo personal sairia igual, com a estética do
 * Welltrainer, e o lime da nossa marca no botão de compra dele.
 *
 * Por que também não é `Button` do shadcn com `style` por cima, que era o que
 * havia: o componente do tema antigo traz por baixo o que o `style` não cobre.
 * O placeholder, o anel de foco e o estado desativado saíam com a cor do **app**
 * dentro da página do cliente, e no escuro isso virava campo preto em página
 * clara.
 *
 * O que estas peças garantem, e o que estava faltando:
 *
 * - **Foco visível.** A página inteira tinha zero `focus-visible`: quem navega
 *   por teclado não via onde estava. O anel é calculado do tema do cliente.
 * - **Alvo de dedo.** 48px de altura. A página é aberta no celular na esmagadora
 *   maioria das vezes, e havia controle de 36px.
 * - **Uma implementação só.** A prévia do construtor e a página no ar usam a
 *   mesma peça: eram duas, e divergiram (o botão de compra da prévia chegou a
 *   escrever "Preço definido na configuração").
 */

/** Cor de texto que se lê sobre um fundo. Decide entre o claro e o escuro do tema. */
function sobre(fundo: string, theme: PageTheme): string {
  const hex = fundo.replace("#", "")
  if (hex.length !== 6) return theme.foreground
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  // Luminância relativa simplificada: acima de 0,6 o fundo é claro.
  const luz = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luz > 0.6 ? "#111111" : "#FFFFFF"
}

export function BotaoDaPagina({
  theme,
  children,
  tom = "principal",
  type = "button",
  desativado,
  carregando,
  onClick,
  href,
  className,
  larguraTotal = true,
  tamanho = "normal",
}: {
  theme: PageTheme
  children: React.ReactNode
  /** `principal` é o botão que vende; `contorno` é a segunda opção da tela. */
  tom?: "principal" | "contorno"
  type?: "button" | "submit"
  desativado?: boolean
  carregando?: boolean
  onClick?: () => void
  /** Quando existe, sai como link (é o "quero saber mais" que leva a outro lugar). */
  href?: string
  className?: string
  larguraTotal?: boolean
  tamanho?: "normal" | "grande"
}) {
  const raio = radiusValue(theme.radius === "full" ? "full" : theme.radius)
  const principal = tom === "principal"
  const corTexto = principal ? theme.primary_fg || sobre(theme.primary, theme) : theme.foreground

  const estilo: React.CSSProperties = {
    background: principal ? theme.primary : "transparent",
    color: corTexto,
    border: principal ? "none" : `1px solid ${theme.border}`,
    borderRadius: raio,
    /* O anel de foco sai do tema do cliente: anel da nossa cor numa página lilás
       lê como defeito. */
    outlineColor: theme.primary,
  }

  const classe = [
    "inline-flex items-center justify-center gap-2 font-semibold transition-opacity",
    /* 48px é o piso de alvo no toque, e esta página vive no celular. */
    tamanho === "grande" ? "min-h-[56px] px-6 text-[1.0625rem]" : "min-h-12 px-5 text-[1rem]",
    larguraTotal ? "w-full" : "",
    desativado || carregando ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:opacity-90 active:opacity-80",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
    className || "",
  ].join(" ")

  if (href && !desativado) {
    return (
      <a href={href} className={classe} style={estilo}>
        {children}
      </a>
    )
  }

  return (
    <button type={type} disabled={desativado || carregando} onClick={onClick} className={classe} style={estilo}>
      {carregando ? "Um instante..." : children}
    </button>
  )
}

export function CampoDaPagina({
  theme,
  valor,
  onChange,
  rotulo,
  placeholder,
  tipo = "text",
  teclado,
  obrigatorio,
  desativado,
  somenteLeitura,
  id,
  ajuda,
  erro,
  sufixo,
  autoComplete,
  aoFocar,
  aoSair,
}: {
  theme: PageTheme
  /**
   * Com `tipo="tel"` é o telefone em DÍGITOS (`11999999999`), e não o texto com
   * a máscara: a máscara é só o que aparece na tela, e quem envia continua
   * mandando número para a API.
   */
  valor: string
  onChange: (v: string) => void
  /** Nome do campo, escrito acima. Placeholder desaparece ao digitar. */
  rotulo?: string
  placeholder?: string
  tipo?: "text" | "email" | "tel"
  teclado?: "email" | "tel" | "numeric"
  obrigatorio?: boolean
  desativado?: boolean
  /**
   * Para mostrar um texto que se copia à mão, como o código do Pix quando o
   * navegador recusou copiar (27/10/2026). Diferente de `desativado`: o texto
   * continua inteiro, nítido e selecionável.
   */
  somenteLeitura?: boolean
  id?: string
  /**
   * Uma frase embaixo do campo, de para que ele serve ("O Pix pede o seu
   * celular"). Some quando há erro: as duas no mesmo lugar brigariam.
   */
  ajuda?: string
  /**
   * O erro do campo, escrito embaixo dele e na borda. É o mesmo lugar do erro do
   * [input](../design-new/primitivos.tsx) do painel: erro que só aparece num aviso
   * longe do campo passa despercebido.
   */
  erro?: string | null
  /** Texto curto colado à direita, dentro da caixa: a bandeira do cartão. */
  sufixo?: string | null
  /** O que o navegador pode preencher sozinho (`cc-number`, `postal-code`). */
  autoComplete?: string
  aoFocar?: () => void
  /** Quando o campo perde o foco: é onde a conferência roda, e não a cada tecla. */
  aoSair?: () => void
}) {
  const raio = radiusValue(theme.radius === "full" ? "lg" : theme.radius)
  /* O vermelho da página: o semântico do sistema, que rende 4,6:1 sobre branco. A
     página do personal não tem as variáveis do painel, e o erro não é cor de marca:
     é o mesmo em toda página, como já era o "Este CPF não é válido" do checkout. */
  const corDoErro = "#DE2828"
  /* O rótulo amarrado ao campo mesmo sem `id` de quem chama (27/10/2026): o formulário
     de contato da página sem nada montado não passava, e o leitor de tela lia um campo
     sem nome (só o placeholder, que some ao digitar). */
  const idGerado = useId()
  const idDoCampo = id ?? idGerado
  const idDoApoio = `${idDoCampo}-apoio`
  const textoDeBaixo = erro || ajuda
  /* O telefone se escreve sozinho, e não campo por campo.

     Os quatro formulários da página (o bloco de formulário, o fim do quiz, a
     captação e o checkout do produto) mandavam `tipo="tel"` com o placeholder
     "(00) 00000-0000" e mais nada: quem digitava via "121212121212" correndo
     na caixa, sem parêntese, sem traço e sem limite. O CPF ao lado já vinha
     escrito, pelo `mascaraCpf` do call site, e era justo isso que cada tela
     tinha de lembrar. Aqui a peça lembra por todas. */
  const ehTelefone = tipo === "tel"
  const naTela = ehTelefone ? telefoneBREscrito(valor) : valor

  return (
    <div className="space-y-1 text-left">
      {rotulo && (
        <label htmlFor={idDoCampo} className="block text-[0.8125rem]" style={{ color: theme.muted }}>
          {rotulo}
        </label>
      )}
      <div className="relative">
        <input
          id={idDoCampo}
          type={tipo}
          inputMode={teclado}
          autoComplete={autoComplete}
          value={naTela}
          onChange={(e) =>
            onChange(ehTelefone ? digitosDeTelefoneBR(e.target.value) : e.target.value)
          }
          onFocus={aoFocar}
          onBlur={aoSair}
          placeholder={placeholder}
          required={obrigatorio}
          disabled={desativado}
          readOnly={somenteLeitura}
          aria-invalid={erro ? true : undefined}
          aria-describedby={textoDeBaixo ? idDoApoio : undefined}
          /* `min-h-12` pelo alvo, `text-[1rem]` porque abaixo de 16px o iPhone dá
             zoom no campo e a página salta. */
          className={`w-full min-h-12 px-3 text-[1rem] outline-none transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 disabled:opacity-60 ${sufixo ? "pr-40" : ""}`}
          style={{
            background: theme.background,
            border: `1px solid ${erro ? corDoErro : theme.border}`,
            color: theme.foreground,
            borderRadius: raio,
            outlineColor: erro ? corDoErro : theme.primary,
          }}
        />
        {sufixo && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[1rem]"
            style={{ color: theme.muted }}
          >
            {sufixo}
          </span>
        )}
      </div>
      {textoDeBaixo && (
        <p
          id={idDoApoio}
          className="text-[0.8125rem] leading-5"
          style={{ color: erro ? corDoErro : theme.muted }}
        >
          {textoDeBaixo}
        </p>
      )}
    </div>
  )
}

/**
 * Acordeão da página do cliente.
 *
 * Mesmas decisões do [acordeão do sistema](../design-new/acordeao.tsx), porque
 * elas não são de estilo, são de entendimento:
 *
 * - **Mais e menos, e não seta.** A seta diz "tem coisa aqui embaixo"; o mais e o
 *   menos dizem "isto abre" e "isto fecha". O ícone gira 45 graus, então o
 *   movimento também conta a mudança.
 * - **Fechado por padrão, uma seção aberta por vez.** Acordeão existe para
 *   esconder o que não é o assunto agora.
 * - **Alvo de 48px** e foco visível, que a versão feita à mão não tinha.
 *
 * O que muda em relação ao do painel: as cores e o arredondamento vêm do tema do
 * personal. A forma é nossa, a cara é dele.
 */
export function AcordeaoDaPagina({
  theme,
  itens,
  fonteTitulo,
  fonteCorpo,
}: {
  theme: PageTheme
  itens: { id: string; titulo: string; conteudo: React.ReactNode }[]
  fonteTitulo?: string
  fonteCorpo?: string
}) {
  const raio = radiusValue(theme.radius)

  return (
    <AccordionPrimitive.Root type="single" collapsible className="space-y-2">
      {itens.map((item) => (
        <AccordionPrimitive.Item
          key={item.id}
          value={item.id}
          className="overflow-hidden"
          style={{
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: raio,
          }}
        >
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
              className="group flex min-h-12 w-full items-center gap-3 px-4 py-3 text-left outline-none transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2"
              style={{ outlineColor: theme.primary }}
            >
              <Icone
                nome={ICONE.criar}
                tamanho={20}
                className="shrink-0 transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-data-[state=open]:rotate-45"
                style={{ color: theme.primary }}
              />
              <span
                className={`min-w-0 flex-1 text-[1rem] font-semibold ${fonteTitulo || ""}`}
                style={{ color: theme.foreground }}
              >
                {item.titulo}
              </span>
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>

          <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div
              className={`px-4 pb-4 pl-[52px] text-[0.9375rem] leading-6 ${fonteCorpo || ""}`}
              style={{ color: theme.muted }}
            >
              {item.conteudo}
            </div>
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      ))}
    </AccordionPrimitive.Root>
  )
}

/**
 * Caixa de conteúdo da página: depoimento, benefício, serviço, garantia.
 *
 * Existe pelo mesmo motivo do [card](../design-new/card.md) do painel: as caixas
 * estavam escritas oito vezes, cada uma com o seu padding e o seu raio, e lado a
 * lado na mesma página pareciam de produtos diferentes. Aqui a medida é uma só
 * (16px de padding, 20px quando `folgada`) e as cores vêm do tema.
 */
export function CaixaDaPagina({
  theme,
  children,
  folgada,
  destacada,
  className,
  style,
}: {
  theme: PageTheme
  children: React.ReactNode
  folgada?: boolean
  /** Borda na cor de destaque: é a caixa que vende (garantia, preço). */
  destacada?: boolean
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <div
      className={`${folgada ? "p-5" : "p-4"} ${className || ""}`}
      style={{
        background: destacada ? `${theme.primary}14` : theme.surface,
        border: `1px solid ${destacada ? `${theme.primary}40` : theme.border}`,
        borderRadius: radiusValue(theme.radius),
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/**
 * Selo da página: a etiqueta curta acima do título.
 *
 * Sempre em cápsula e sempre com texto: o [selo](../design-new/selo.md) do painel
 * tem a mesma regra, e um selo sem palavra é só uma bolinha colorida.
 */
export function SeloDaPagina({
  theme,
  children,
  estilo = "suave",
}: {
  theme: PageTheme
  children: React.ReactNode
  estilo?: "suave" | "solido" | "contorno"
}) {
  const cores =
    estilo === "solido"
      ? { background: theme.primary, color: theme.primary_fg, border: "1px solid transparent" }
      : estilo === "contorno"
        ? { background: "transparent", color: theme.foreground, border: `1px solid ${theme.border}` }
        : { background: `${theme.primary}1F`, color: theme.foreground, border: "1px solid transparent" }

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[0.8125rem] font-medium"
      style={cores}
    >
      {children}
    </span>
  )
}

/**
 * Opção escolhível da página: as respostas do quiz, a forma de pagamento.
 *
 * Espelha a [lista de escolha](../design-new/lista-escolha.md) do painel: a
 * **linha inteira** é o alvo, a marca fica à esquerda e o rótulo vai escrito.
 * O que faltava na versão feita à mão: foco visível e a semântica (o leitor de
 * tela anunciava "botão", sem dizer se estava marcado).
 */
export function OpcaoDaPagina({
  theme,
  children,
  marcada,
  varias,
  onClick,
  fonte,
}: {
  theme: PageTheme
  children: React.ReactNode
  marcada: boolean
  /** Quando dá para marcar mais de uma: a marca vira quadrada. */
  varias?: boolean
  onClick: () => void
  fonte?: string
}) {
  return (
    <button
      type="button"
      role={varias ? "checkbox" : "radio"}
      aria-checked={marcada}
      onClick={onClick}
      className="flex min-h-12 w-full items-center gap-3 px-4 py-3 text-left transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:opacity-80"
      style={{
        background: marcada ? `${theme.primary}1A` : theme.background,
        border: `2px solid ${marcada ? theme.primary : theme.border}`,
        borderRadius: radiusValue(theme.radius === "full" ? "lg" : theme.radius),
        outlineColor: theme.primary,
      }}
    >
      <span
        aria-hidden
        className="grid size-5 shrink-0 place-items-center"
        style={{
          border: `2px solid ${marcada ? theme.primary : theme.muted}`,
          background: marcada ? theme.primary : "transparent",
          borderRadius: varias ? 6 : 9999,
        }}
      >
        {marcada && (
          <Icone
            nome={ICONE.salvar}
            tamanho={16}
            style={{ color: theme.primary_fg || "#FFFFFF" }}
          />
        )}
      </span>
      <span className={`text-[1rem] ${fonte || ""}`} style={{ color: theme.foreground }}>
        {children}
      </span>
    </button>
  )
}

/**
 * A oferta do pagamento (order bump, 13/10/2026): "leve também", marcada com um
 * toque logo acima do botão de pagar.
 *
 * É a [opção](#OpcaoDaPagina) com o que uma oferta precisa para vender: a capa, a
 * chamada, uma linha e o preço, com o "de" riscado quando ela sai mais barata. A
 * borda tracejada antes de marcar diz "isto é um extra", como nos checkouts que
 * quem compra on-line já conhece; marcada, ela fica cheia na cor do tema. O preço
 * vem com "+" porque ele SOMA no total do botão de pagar.
 */
export function OfertaDaPagina({
  theme,
  marcada,
  onClick,
  titulo,
  texto,
  imagem,
  valor,
  valorDe,
  fonteTitulo,
}: {
  theme: PageTheme
  marcada: boolean
  onClick: () => void
  titulo: string
  texto?: string | null
  imagem?: string | null
  valor: number
  /** O preço normal do produto, quando a oferta sai mais barata. */
  valorDe?: number | null
  fonteTitulo?: string
}) {
  const raio = radiusValue(theme.radius === "full" ? "lg" : theme.radius)
  const reais = (v: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const destaque = theme.primary === theme.background ? theme.foreground : theme.primary
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={marcada}
      aria-label={`${titulo}. ${valorDe != null ? `De R$ ${reais(valorDe)} por` : "Por"} mais R$ ${reais(valor)}.`}
      onClick={onClick}
      className="flex w-full items-start gap-3 p-4 text-left transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:opacity-80"
      style={{
        background: marcada ? `${theme.primary}1A` : theme.surface,
        border: `2px ${marcada ? "solid" : "dashed"} ${marcada ? theme.primary : theme.border}`,
        borderRadius: raio,
        outlineColor: theme.primary,
      }}
    >
      <span
        aria-hidden
        className="mt-0.5 grid size-6 shrink-0 place-items-center"
        style={{
          border: `2px solid ${marcada ? theme.primary : theme.muted}`,
          background: marcada ? theme.primary : theme.background,
          borderRadius: 6,
        }}
      >
        {marcada && <Icone nome={ICONE.salvar} tamanho={16} style={{ color: theme.primary_fg || "#FFFFFF" }} />}
      </span>
      {imagem && (
        <img
          src={imagem}
          alt=""
          aria-hidden
          className="size-14 shrink-0 object-cover"
          style={{ borderRadius: radiusValue(theme.radius === "full" ? "md" : theme.radius) }}
        />
      )}
      <span aria-hidden className="min-w-0 flex-1">
        <span
          className={`block break-words text-[1rem] font-semibold leading-6 ${fonteTitulo || ""}`}
          style={{ color: theme.foreground }}
        >
          {titulo}
        </span>
        {texto && (
          <span className="mt-1 block break-words text-[0.875rem] leading-5" style={{ color: theme.muted }}>
            {texto}
          </span>
        )}
        <span className="mt-2 flex flex-wrap items-baseline gap-x-2 text-[1rem]">
          {valorDe != null && (
            <s className="text-[0.875rem]" style={{ color: theme.muted }}>
              R$ {reais(valorDe)}
            </s>
          )}
          <span className="font-bold" style={{ color: destaque }}>
            + R$ {reais(valor)}
          </span>
        </span>
      </span>
    </button>
  )
}

/*
  ─── Escala de texto e de espaço da página ───

  Existe porque a página tinha **nove** tamanhos de fonte (de 10px a 30px, sem
  escala) e **cinco** espaçamentos verticais diferentes entre blocos. Lado a lado,
  dois blocos pareciam de sites diferentes: um com respiro, o outro apertado.

  A hierarquia também estava embaralhada: `h3` e `h4` no mesmo nível visual, sem
  nenhum `h2`, e o único `h1` no cabeçalho. Para quem navega por leitor de tela, a
  página não tinha estrutura.

  Os tamanhos são fixos, e não responsivos: a página é vista no celular na
  esmagadora maioria das vezes, e o corpo de 16px é o mesmo nos dois lugares.
*/

type PapelDaPagina = "promessa" | "secao" | "corpo" | "apoio" | "micro"

const ESCALA: Record<PapelDaPagina, { tamanho: string; entrelinha: string; peso: number; tracking?: string }> = {
  /** O título do cabeçalho: a promessa, a maior coisa da página. */
  promessa: { tamanho: "1.875rem", entrelinha: "2.25rem", peso: 700, tracking: "-0.02em" },
  /** Título de bloco: "O que você recebe", "Perguntas frequentes". */
  secao: { tamanho: "1.375rem", entrelinha: "1.75rem", peso: 700, tracking: "-0.01em" },
  corpo: { tamanho: "1rem", entrelinha: "1.5rem", peso: 400 },
  apoio: { tamanho: "0.875rem", entrelinha: "1.25rem", peso: 400 },
  micro: { tamanho: "0.8125rem", entrelinha: "1.25rem", peso: 400 },
}

export function TextoDaPagina({
  theme,
  papel = "corpo",
  como,
  cor,
  fonte,
  alinhamento,
  children,
  className,
}: {
  theme: PageTheme
  papel?: PapelDaPagina
  /** O papel define a aparência; a semântica vem daqui. */
  como?: "h1" | "h2" | "h3" | "p" | "span" | "div"
  /** Sobrescreve a cor do papel. Padrão: texto para título e corpo, apoio no resto. */
  cor?: string
  fonte?: string
  alinhamento?: "left" | "center" | "right"
  children: React.ReactNode
  className?: string
}) {
  const m = ESCALA[papel]
  const Tag = como ?? (papel === "promessa" ? "h1" : papel === "secao" ? "h2" : "p")
  const corPadrao = papel === "apoio" || papel === "micro" ? theme.muted : theme.foreground

  return (
    <Tag
      className={`${fonte || ""} ${alinhamento ? `text-${alinhamento}` : ""} ${className || ""}`}
      style={{
        fontSize: m.tamanho,
        lineHeight: m.entrelinha,
        fontWeight: m.peso,
        letterSpacing: m.tracking,
        color: cor ?? corPadrao,
        /* Título equilibra as linhas; corpo evita palavra órfã na última. */
        textWrap: papel === "promessa" || papel === "secao" ? "balance" : "pretty",
      }}
    >
      {children}
    </Tag>
  )
}

/**
 * O aviso de que algo não deu certo (27/10/2026): o formulário que não enviou diz
 * por quê, logo acima do botão, no lugar do silêncio que fazia o visitante desistir
 * ou mandar quatro vezes. `role="alert"`, para o leitor de tela ler na hora.
 *
 * Na cor do TEXTO do tema, numa caixa com a borda dele, e não no vermelho de fora
 * nem na cor de destaque: a página é do personal, e lime sobre branco (o destaque do
 * tema "white") não se lê.
 */
/**
 * A frase do aviso quando o envio de um formulário da página falha: o motivo que o
 * servidor deu ("Confira o e-mail: ele não parece completo."), ou o que fazer quando
 * nem chegou lá.
 */
export function motivoDoEnvio(falha: unknown): string {
  const texto = falha instanceof Error ? falha.message : ""
  if (!texto || /failed to fetch|networkerror|load failed/i.test(texto)) {
    return "Não deu para enviar agora. Confira a internet e tente de novo."
  }
  return `Não deu para enviar. ${texto}`
}

export function AvisoDaPagina({ theme, children }: { theme: PageTheme; children: React.ReactNode }) {
  return (
    <div role="alert" className="rounded-[8px] border px-3 py-2" style={{ borderColor: theme.border, background: theme.surface }}>
      <TextoDaPagina theme={theme} papel="apoio" cor={theme.foreground}>
        {children}
      </TextoDaPagina>
    </div>
  )
}

/**
 * A faixa de um bloco na página.
 *
 * Um espaçamento vertical só, para os blocos terem ritmo: eram cinco valores
 * diferentes, e a página parecia montada por pessoas distintas. `respiro="mais"`
 * é para o cabeçalho e o botão de compra, que pedem ar em volta.
 */
export function SecaoDaPagina({
  children,
  respiro = "normal",
  id,
  className,
}: {
  children: React.ReactNode
  respiro?: "normal" | "mais" | "nenhum"
  id?: string
  className?: string
}) {
  const py = respiro === "mais" ? "py-8" : respiro === "nenhum" ? "py-0" : "py-5"
  return (
    <div id={id} className={`px-4 ${py} ${className || ""}`}>
      {children}
    </div>
  )
}

/**
 * Imagem da página, com o espaço reservado antes de carregar.
 *
 * Mesma razão da [imagem](../design-new/imagem.md) do painel: sem proporção
 * declarada, a página **salta** quando cada foto chega, e quem está lendo perde a
 * linha. As fotos aqui vêm do personal, em tamanhos que ninguém controla.
 *
 * A foto do bloco Imagem e as do quiz passam a proporção **da própria foto**
 * (medida no envio) com `ajuste="conter"`: aparecem inteiras, sem corte. Sem a
 * medida (foto antiga), a caixa fica sem proporção e a foto entra na altura dela.
 */
export function ImagemDaPagina({
  theme,
  src,
  alt,
  proporcao,
  ajuste = "cobrir",
  arredondar = true,
  className,
}: {
  theme: PageTheme
  src?: string | null
  /** Vazio quando a imagem é decorativa: aí o leitor de tela a ignora. */
  alt: string
  /**
   * Ex.: "16/9", "1/1", "1080/1350". Sem isto a foto entra inteira na altura
   * dela, mas a página salta quando ela carrega.
   */
  proporcao?: string
  ajuste?: "cobrir" | "conter"
  arredondar?: boolean
  className?: string
}) {
  const raio = arredondar ? radiusValue(theme.radius) : 0

  return (
    <span
      className={`block overflow-hidden ${className || ""}`}
      style={{
        aspectRatio: proporcao,
        borderRadius: raio,
        background: theme.surface,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          aria-hidden={alt === "" ? true : undefined}
          loading="lazy"
          /* Sem proporção, a altura é a da foto: `h-full` aqui dependeria da
             altura do pai, que é a própria foto. */
          className={proporcao ? "size-full" : "h-auto w-full"}
          style={{ objectFit: ajuste === "cobrir" ? "cover" : "contain" }}
        />
      ) : (
        /* Reserva com ícone: caixa vazia deixa a pessoa achando que a página
           está quebrada. */
        <span className="grid size-full place-items-center" style={{ color: theme.muted }}>
          <Icone nome={ICONE.imagem} tamanho={32} />
        </span>
      )}
    </span>
  )
}

/** Avatar redondo (a foto do personal no cabeçalho). */
export function AvatarDaPagina({
  theme,
  src,
  alt,
  tamanho = 88,
}: {
  theme: PageTheme
  src?: string | null
  alt: string
  tamanho?: number
}) {
  return (
    <span
      className="block shrink-0 overflow-hidden rounded-full"
      style={{ width: tamanho, height: tamanho, background: theme.surface }}
    >
      {src && <img src={src} alt={alt} className="size-full object-cover" loading="lazy" />}
    </span>
  )
}

/**
 * Nota em estrelas do depoimento.
 *
 * A nota vai também **escrita** no `aria-label`: cinco desenhos iguais não são
 * lidos por leitor de tela, e quem não distingue as cores não conta as cheias.
 */
export function EstrelasDaPagina({ theme, nota, de = 5 }: { theme: PageTheme; nota: number; de?: number }) {
  return (
    <span className="flex gap-0.5" role="img" aria-label={`${nota} de ${de} estrelas`}>
      {Array.from({ length: de }, (_, i) => (
        <Icone
          key={i}
          nome={ICONE.estrela}
          tamanho={16}
          preenchido={i < nota}
          style={{ color: i < nota ? theme.primary : theme.border }}
        />
      ))}
    </span>
  )
}

/** Quanto falta, com o número escrito ao lado (barra sozinha não informa). */
export function ProgressoDaPagina({
  theme,
  parte,
  total,
  rotulo,
}: {
  theme: PageTheme
  parte: number
  total: number
  rotulo?: string
}) {
  const pct = total > 0 ? Math.round((parte / total) * 100) : 0
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <TextoDaPagina theme={theme} papel="micro">
          {rotulo ?? `Pergunta ${parte} de ${total}`}
        </TextoDaPagina>
        <TextoDaPagina theme={theme} papel="micro">{`${pct}%`}</TextoDaPagina>
      </div>
      <span
        className="block h-2 overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={parte}
        aria-valuemin={0}
        aria-valuemax={total}
        style={{ background: theme.border }}
      >
        <span
          className="block h-full rounded-full transition-[width] duration-200"
          style={{ width: `${pct}%`, background: theme.primary }}
        />
      </span>
    </div>
  )
}

/** Linha que separa dois assuntos. Espaço vem antes de linha, como no painel. */
export function DivisorDaPagina({ theme, className }: { theme: PageTheme; className?: string }) {
  return <span aria-hidden className={`block h-px w-full ${className || ""}`} style={{ background: theme.border }} />
}

/** De onde veio quem clicou na assinatura: o perfil do personal ou a página de um produto dele. */
export type PaginaDaAssinatura = "perfil" | "produto"

/**
 * O site, com a origem escrita. `wellt.fit` e `convite` são a fonte e a mídia que
 * a atribuição já chama de "Indicação de cliente" (`origem-por-referrer.ts` na API):
 * quem chega por aqui veio pela página de um cliente nosso, e o cadastro cai no
 * canal certo em vez de "direto".
 */
function enderecoDoSite(pagina: PaginaDaAssinatura): string {
  return `https://welltrainer.com.br/?utm_source=wellt.fit&utm_medium=convite&utm_campaign=assinatura-da-pagina&utm_content=${pagina}`
}

/**
 * A assinatura do rodapé: a linha pequena que diz com que ferramenta a página foi
 * feita, com "Welltrainer" levando ao site.
 *
 * Pequena e no fim, porque a página é do personal e a marca aqui é só a origem da
 * ferramenta. Mas é LINK, e não texto solto: as páginas dos personais recebem link
 * da bio, do site e do Linktree deles, e eram um beco sem saída. Agora a força
 * desses links passa adiante para o site, que é onde a busca precisa dela, e quem
 * gostou da página acha de onde ela veio. A âncora é a marca, nunca "app para
 * personal trainer": a mesma frase de busca no rodapé de centenas de páginas é o
 * que o Google trata como link fabricado.
 *
 * Segue o [link](../../../design/componentes/link.md) do painel: o sublinhado é o
 * sinal, a cor vem do texto em volta e o sublinhado some no hover. A cor do foco sai
 * do tema do cliente, como em todas as peças daqui. Sem `nofollow`, de propósito.
 *
 * Abre na MESMA aba, e por isso sem o ícone de outra aba: o menor ícone da escala
 * tem 16px, maior que o texto de 11px da assinatura, e a nossa marca viraria a coisa
 * mais pesada do rodapé da página de outra pessoa. Quem clica aqui quer saber da
 * ferramenta, e o voltar do navegador devolve a página do personal.
 */
export function AssinaturaDaPagina({
  theme,
  pagina,
  antes = "Feito com",
  className,
}: {
  theme: PageTheme
  pagina: PaginaDaAssinatura
  /** O texto antes da marca. "Feito com" no perfil, "Processado com segurança via" na compra. */
  antes?: string
  className?: string
}) {
  return (
    <p className={`text-center text-[0.6875rem] ${className || ""}`} style={{ color: theme.muted }}>
      {antes}{" "}
      <a
        href={enderecoDoSite(pagina)}
        className="rounded-sm underline decoration-1 underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        style={{ color: "inherit", outlineColor: theme.primary }}
      >
        Welltrainer
      </a>
    </p>
  )
}

/**
 * O cartão de crédito na página de vendas do personal (o split no Pagar.me,
 * 21/10/2026): número, nome, validade, código de segurança e o endereço da fatura.
 *
 * É o [campo do cartão](../design-new/campo-cartao.tsx) do sistema com a roupa da
 * página: as regras (a bandeira escrita, a máscara que é o limite, a conta do
 * último dígito, o erro ao sair do campo, onde fica o código) moram em
 * `lib/utils/cartao.ts` e são as mesmas; aqui só muda a cor, a fonte e a forma, que
 * são do personal. O CEP busca o endereço no oitavo dígito, como o do painel.
 *
 * O número nunca passa pela nossa API: quem chama transforma o cartão em token no
 * Pagar.me (`pagarme-service.ts`) e manda só o token. A peça não guarda nada.
 */
export function CartaoDaPagina({
  theme,
  cartao,
  aoMudarCartao,
  endereco,
  aoMudarEndereco,
  erros,
  desativado,
  indisponivel,
  idBase = "cartao",
}: {
  theme: PageTheme
  cartao: CartaoDigitado
  aoMudarCartao: (c: CartaoDigitado) => void
  endereco: EnderecoPayload
  aoMudarEndereco: (e: EnderecoPayload) => void
  /** Erro de fora, por campo: o Pagar.me recusou um dado ao conferir o cartão. */
  erros?: Partial<Record<CampoDoCartao, string>>
  desativado?: boolean
  /** Sem a chave pública do Pagar.me no ambiente: o aviso no lugar dos campos. */
  indisponivel?: boolean
  /** Prefixo dos `id` dos campos: a compra e a oferta depois dela têm cada uma o seu. */
  idBase?: string
}) {
  const [saiu, setSaiu] = useState<Partial<Record<CampoDoCartao | CampoDeEndereco, boolean>>>({})
  const marcar = (campo: CampoDoCartao | CampoDeEndereco, valor: boolean) =>
    setSaiu((s) => ({ ...s, [campo]: valor }))
  const { consultar, loading: buscandoCep } = useViaCep()
  const [cepNaoEncontrado, setCepNaoEncontrado] = useState(false)
  const ultimoCep = useRef("")
  /* A busca do CEP volta depois de um tempo: o endereço que ela completa é o de
     AGORA, e não o do desenho em que ela começou. */
  const enderecoAtual = useRef(endereco)
  enderecoAtual.current = endereco
  const mudarEnderecoAtual = useRef(aoMudarEndereco)
  mudarEnderecoAtual.current = aoMudarEndereco

  useEffect(() => {
    const digitos = endereco.cep.replace(/\D/g, "")
    if (digitos.length !== 8) {
      setCepNaoEncontrado(false)
      ultimoCep.current = ""
      return
    }
    if (ultimoCep.current === digitos) return
    ultimoCep.current = digitos
    let cancelado = false
    void consultar(digitos).then((achado) => {
      if (cancelado) return
      if (!achado) {
        setCepNaoEncontrado(true)
        return
      }
      setCepNaoEncontrado(false)
      const atual = enderecoAtual.current
      mudarEnderecoAtual.current({
        ...atual,
        logradouro: achado.logradouro || atual.logradouro,
        bairro: achado.bairro || atual.bairro,
        cidade: achado.cidade || atual.cidade,
        estado: achado.estado || atual.estado,
      })
    })
    return () => {
      cancelado = true
    }
  }, [endereco.cep, consultar])

  if (indisponivel) {
    return (
      <CaixaDaPagina theme={theme}>
        <TextoDaPagina theme={theme} papel="corpo">
          O pagamento no cartão está indisponível agora. Pague no Pix.
        </TextoDaPagina>
      </CaixaDaPagina>
    )
  }

  const bandeira = bandeiraDoCartao(cartao.numero)
  const nomeDaBandeira = bandeira ? NOME_DA_BANDEIRA[bandeira] : null
  const proprios = errosDoCartao(cartao)
  const erroDoCartao = (campo: CampoDoCartao) => erros?.[campo] ?? (saiu[campo] ? proprios[campo] : null)

  const mudarCartao = (campo: CampoDoCartao, valor: string) => {
    const novo = { ...cartao, [campo]: valor }
    /* Trocar de bandeira muda o tamanho do código: o que sobrou do Amex não vale num Visa. */
    if (campo === "numero") novo.codigo = mascaraCodigo(novo.codigo, bandeiraDoCartao(valor))
    aoMudarCartao(novo)
  }

  /* O endereço acusa a falta como o do painel: na hora, quando ele já tem alguma
     coisa (o CEP trouxe o resto); em branco, só no campo por onde a pessoa passou. */
  const faltas = faltasDoEndereco(endereco)
  const comecado = Boolean(endereco.cep.trim() || endereco.logradouro.trim() || endereco.cidade.trim())
  const erroDoEndereco = (campo: CampoDeEndereco) =>
    faltas.includes(campo) && (comecado || saiu[campo]) ? `Preencha ${NOME_DO_CAMPO_DE_ENDERECO[campo]}.` : null
  const mudarEndereco = (campo: keyof EnderecoPayload, valor: string) =>
    aoMudarEndereco({ ...endereco, [campo]: valor })

  return (
    <div className="space-y-3 text-left">
      <CampoDaPagina
        theme={theme}
        id={`${idBase}-numero`}
        rotulo="Número do cartão"
        teclado="numeric"
        autoComplete="cc-number"
        placeholder="0000 0000 0000 0000"
        valor={cartao.numero}
        onChange={(v) => mudarCartao("numero", mascaraNumeroDoCartao(v))}
        sufixo={nomeDaBandeira}
        erro={erroDoCartao("numero")}
        aoFocar={() => marcar("numero", false)}
        aoSair={() => marcar("numero", true)}
        desativado={desativado}
        obrigatorio
      />
      {/* A bandeira na caixa é decorativa: aqui ela é dita ao leitor de tela. */}
      <span className="sr-only" aria-live="polite">
        {nomeDaBandeira ? `Cartão ${nomeDaBandeira}` : ""}
      </span>
      <CampoDaPagina
        theme={theme}
        id={`${idBase}-nome`}
        rotulo="Nome como está no cartão"
        autoComplete="cc-name"
        valor={cartao.nome}
        onChange={(v) => mudarCartao("nome", v)}
        erro={erroDoCartao("nome")}
        aoFocar={() => marcar("nome", false)}
        aoSair={() => marcar("nome", true)}
        desativado={desativado}
        obrigatorio
      />
      <div className="grid grid-cols-2 gap-3">
        <CampoDaPagina
          theme={theme}
          id={`${idBase}-validade`}
          rotulo="Validade"
          teclado="numeric"
          autoComplete="cc-exp"
          placeholder="MM/AA"
          valor={cartao.validade}
          onChange={(v) => mudarCartao("validade", mascaraValidade(v))}
          erro={erroDoCartao("validade")}
          aoFocar={() => marcar("validade", false)}
          aoSair={() => marcar("validade", true)}
          desativado={desativado}
          obrigatorio
        />
        <CampoDaPagina
          theme={theme}
          id={`${idBase}-codigo`}
          rotulo="Código de segurança"
          teclado="numeric"
          autoComplete="cc-csc"
          placeholder={tamanhoDoCodigo(bandeira) === 4 ? "0000" : "000"}
          valor={cartao.codigo}
          onChange={(v) => mudarCartao("codigo", mascaraCodigo(v, bandeira))}
          erro={erroDoCartao("codigo")}
          ajuda={ondeFicaOCodigo(bandeira)}
          aoFocar={() => marcar("codigo", false)}
          aoSair={() => marcar("codigo", true)}
          desativado={desativado}
          obrigatorio
        />
      </div>

      <div className="pt-2">
        <TextoDaPagina theme={theme} papel="apoio" cor={theme.foreground} className="font-semibold">
          Endereço da fatura
        </TextoDaPagina>
        <TextoDaPagina theme={theme} papel="micro">
          O banco confere o endereço para aprovar a compra.
        </TextoDaPagina>
      </div>
      <CampoDaPagina
        theme={theme}
        id={`${idBase}-cep`}
        rotulo="CEP"
        teclado="numeric"
        autoComplete="postal-code"
        placeholder="00000-000"
        valor={mascaraCep(endereco.cep)}
        onChange={(v) => mudarEndereco("cep", mascaraCep(v))}
        erro={cepNaoEncontrado ? "Não encontramos este CEP. Confira, ou escreva o endereço à mão." : erroDoEndereco("cep")}
        ajuda={buscandoCep ? "Buscando o endereço..." : "O resto do endereço preenchemos para você."}
        aoSair={() => marcar("cep", true)}
        desativado={desativado}
        obrigatorio
      />
      <CampoDaPagina
        theme={theme}
        id={`${idBase}-rua`}
        rotulo="Rua"
        autoComplete="address-line1"
        valor={endereco.logradouro}
        onChange={(v) => mudarEndereco("logradouro", v)}
        erro={erroDoEndereco("logradouro")}
        aoSair={() => marcar("logradouro", true)}
        desativado={desativado}
        obrigatorio
      />
      <div className="grid grid-cols-[120px_1fr] gap-3">
        <CampoDaPagina
          theme={theme}
          id={`${idBase}-numero-da-casa`}
          rotulo="Número"
          teclado="numeric"
          valor={endereco.numero}
          onChange={(v) => mudarEndereco("numero", v)}
          erro={erroDoEndereco("numero")}
          ajuda="Sem número? Escreva s/n."
          aoSair={() => marcar("numero", true)}
          desativado={desativado}
          obrigatorio
        />
        <CampoDaPagina
          theme={theme}
          id={`${idBase}-bairro`}
          rotulo="Bairro"
          valor={endereco.bairro}
          onChange={(v) => mudarEndereco("bairro", v)}
          erro={erroDoEndereco("bairro")}
          aoSair={() => marcar("bairro", true)}
          desativado={desativado}
          obrigatorio
        />
      </div>
      <div className="grid grid-cols-[1fr_88px] gap-3">
        <CampoDaPagina
          theme={theme}
          id={`${idBase}-cidade`}
          rotulo="Cidade"
          autoComplete="address-level2"
          valor={endereco.cidade}
          onChange={(v) => mudarEndereco("cidade", v)}
          erro={erroDoEndereco("cidade")}
          aoSair={() => marcar("cidade", true)}
          desativado={desativado}
          obrigatorio
        />
        <CampoDaPagina
          theme={theme}
          id={`${idBase}-uf`}
          rotulo="UF"
          autoComplete="address-level1"
          valor={endereco.estado}
          onChange={(v) => mudarEndereco("estado", v.toUpperCase().slice(0, 2))}
          erro={erroDoEndereco("estado")}
          aoSair={() => marcar("estado", true)}
          desativado={desativado}
          obrigatorio
        />
      </div>
    </div>
  )
}

/**
 * A frase de confiança do cartão, colada no botão de pagar, na roupa da página.
 * A mesma do [campo do cartão](../design-new/campo-cartao.tsx): quem processa e o
 * que NÃO fica com a gente.
 */
export function GarantiaDoCartaoDaPagina({ theme }: { theme: PageTheme }) {
  return (
    <p className="flex items-start justify-center gap-2 text-left">
      <Icone nome={ICONE.garantia} tamanho={16} style={{ color: theme.muted, marginTop: 2, flexShrink: 0 }} />
      <TextoDaPagina theme={theme} papel="micro" como="span">
        Os dados do cartão vão direto para a empresa que processa o pagamento. A Welltrainer não guarda o número.
      </TextoDaPagina>
    </p>
  )
}
